import { useMemo } from "react"
import _ from 'lodash'

import { getYYYYMMDD } from "../../helpers/UtilityHelper"
import { useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls"
import { courierPerformanceApi } from "../../redux/courierPerformance"
import { serviceTypeLabel } from "./serviceTypeLabel"

const courierFilter = {
    andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
    offset: 0,
    limit: 50000,
    skipCount: true,
    // serviceTypePrimary/Secondary are required so the perf layer can map courierId → service type
    includes: 'id,name,cpAccountCode,prozoId,supportedShipmentType,type,parent,isExternal,isSddNddEnabled,serviceTypePrimary,serviceTypeSecondary',
}

export function useCourierBenchmark(dateRange, groupOn) {

    const fromDate = getYYYYMMDD(dateRange[0].startDate)
    const toDate = getYYYYMMDD(dateRange[0].endDate)

    const options = { fromDate, toDate }

    // fetched so the perf-layer transform has a courier list carrying serviceTypePrimary
    const { isLoading: isLoadingCouriers, isFetching: isFetchingCouriers } = useGetAllCourierPartnersCachedQuery(courierFilter)
    const skipPerformanceData = useMemo(() => isLoadingCouriers || isFetchingCouriers, [isLoadingCouriers, isFetchingCouriers])

    const { status: deliveryStatus } = courierPerformanceApi.useGetDeliveryMetricsQuery(options, { skip: skipPerformanceData })
    const { status: tatStatus } = courierPerformanceApi.useGetTATMetricsQuery(options, { skip: skipPerformanceData })
    const { status: rvpPickupStatus } = courierPerformanceApi.useGetRVPPickupMetricsQuery(options, { skip: skipPerformanceData })

    const skip = useMemo(() =>
        deliveryStatus !== 'fulfilled' ||
        tatStatus !== 'fulfilled' ||
        rvpPickupStatus !== 'fulfilled',
        [deliveryStatus, tatStatus, rvpPickupStatus]
    )

    const {
        data: courierPerformance,
        isLoading,
        isFetching
    } = courierPerformanceApi.useGetCourierPerformanceQuery({ ...options, groupOn }, { skip })

    const performanceData = useMemo(() => ({
        deliveryPerformance: courierPerformance?.deliveryPerformance,
        tatPerformance: courierPerformance?.tatPerformance,
        rvpPickupPerformance: courierPerformance?.rvpPickupPerformance,
        deliveryPerformanceTrend: courierPerformance?.deliveryPerformanceTrend,
        tatPerformanceTrend: courierPerformance?.tatPerformanceTrend,
        rvpPickupPerformanceTrend: courierPerformance?.rvpPickupPerformanceTrend,
    }), [courierPerformance])

    /* one forward row per SERVICE TYPE present in the delivery metrics (merged with TAT) */
    const forwardBenchmarking = useMemo(() => {
        const dp = performanceData.deliveryPerformance
        const tp = performanceData.tatPerformance
        if (!dp) return []
        const rows = Array.from(dp.keys()).map((serviceType) => ({
            courierId: serviceType,
            name: serviceTypeLabel(serviceType),
            serviceType,
            ...(dp.get(serviceType) || {}),
            ...(tp?.get(serviceType) || {}),
        }))
        return _.orderBy(rows, (i) => i.totalPickedupCount ?? -1, 'desc')
    }, [performanceData.deliveryPerformance, performanceData.tatPerformance])

    /* one reverse row per SERVICE TYPE present in the RVP pickup metrics */
    const reverseBenchmarking = useMemo(() => {
        const rp = performanceData.rvpPickupPerformance
        if (!rp) return []
        const rows = Array.from(rp.keys()).map((serviceType) => ({
            courierId: serviceType,
            name: serviceTypeLabel(serviceType),
            serviceType,
            ...(rp.get(serviceType) || {}),
        }))
        return _.orderBy(rows, (i) => i.totalRVPRequestCount ?? -1, 'desc')
    }, [performanceData.rvpPickupPerformance])


    const isLoadingMemo = useMemo(() =>
        skip || isLoading || isFetching,
        [skip, isLoading, isFetching]
    )

    return {
        forwardBenchmarking,
        reverseBenchmarking,
        courierPerformance,
        isLoading: isLoadingMemo,
    }
}
